import React from 'react';
import { FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DeviceSelect } from '@/console/features/devices/components/device-select/device-select';
import { DocumentSelect } from '@/console/features/documents/components/document-select/document-select';
import { GatewaySelect } from '@/console/features/gateways/components/gateway-select/gateway-select';
import { IntegrationSelect } from '@/console/features/integrations/components/integration-select/integration-select';
import { LicenseSelect } from '@/console/features/licenses/components/license-select/license-select';
import { PipelineSelect } from '@/console/features/pipelines/components/pipeline-select/pipeline-select';
import { ProjectSelect } from '@/console/features/projects/components/project-select/project-select';
import { VaultSelect } from '@/console/features/vaults/components/vault-select/vault-select';
import { WebhookSelect } from '@/console/features/webhooks/components/webhook-select/webhook-select';
import { WorkstationSelect } from '@/console/features/workstations/components/workstation-select/workstation-select';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import { TPolicyEditorInputSchema } from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.types';
import { createIdResourceFilter } from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.utils';
import { TResourceSelectProps } from '@/shared/features/rbac/components/create-policy/resource-select/resource-select.types';
import { usePolicyOptions } from '@/shared/features/rbac/hooks/use-policy-options/use-policy-options';
import {
  useWildCardOption,
  WILD_CARD,
} from '@/shared/features/rbac/hooks/use-wild-card-option/use-wild-card-option';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { DeviceFragment } from '@/shared/types/types.generated';

/**
 * Console's policy-editor resource selectors. Registered per resource type in
 * `consoleRbacConfig.resourceSelectors`. Holds the per-resource-type
 * option/createOption logic that previously lived in the shared `ResourceSelect`.
 */
export const ConsoleResourceSelect: React.FC<TResourceSelectProps> = ({
  resourceType,
  policyIndex,
}) => {
  const { getResourcePrefix } = usePolicyOptions();
  const { handleWildCardSelect } = useWildCardOption();
  const valuePrefix = getResourcePrefix(resourceType);
  const idResourceFilter = createIdResourceFilter(resourceType);
  const { t } = useTranslation();
  const { getVendor } = useVendor();
  const wildCardLabel = t('rbac:all_resources_of_type', {
    resourceType: t(`rbac:resource_type.${resourceType.toLowerCase()}`),
  });
  const name =
    `policies.${policyIndex}.resource` as FieldPath<TPolicyEditorInputSchema>;

  const getWildCardOption = (resourceType: string): TSelectOption<string> => {
    return {
      label: wildCardLabel,
      value: `${resourceType}:${WILD_CARD}`,
    };
  };

  const createOption = (device: DeviceFragment) => {
    return {
      label: device.name,
      value: `${valuePrefix}:${device.deviceId}`,
      additionalInfo: [
        t(`device:device_types.${device.deviceType.toLowerCase()}`),
        device.connectionConfiguration.ipAddress,
      ],
    };
  };

  if (resourceType === ResourceTypeEnum.Device) {
    return (
      <DeviceSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={createOption}
        additionalOptions={[getWildCardOption('device')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Gateway) {
    return (
      <GatewaySelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.gatewayId}`,
          };
        }}
        additionalOptions={[getWildCardOption('gateway')]}
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Document) {
    return (
      <DocumentSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.documentId}`,
          };
        }}
        additionalOptions={[getWildCardOption('document')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Integration) {
    return (
      <IntegrationSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.integrationId}`,
          };
        }}
        additionalOptions={[getWildCardOption('integration')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.License) {
    return (
      <LicenseSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name ?? '',
            value: `${valuePrefix}:${value.licenseId}`,
          };
        }}
        additionalOptions={[getWildCardOption('license')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Vault) {
    return (
      <VaultSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.vaultId}`,
          };
        }}
        multiple
        additionalOptions={[getWildCardOption('vault')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Project) {
    return (
      <ProjectSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.projectId}`,
            additionalInfo: getVendor(value)?.name,
          };
        }}
        additionalOptions={[getWildCardOption('project')]}
        required
        optionProps={{ additionalInfo: { vendor: true, ide: true } }}
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Pipeline) {
    return (
      <PipelineSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.pipelineConfigurationId}`,
          };
        }}
        additionalOptions={[getWildCardOption('pipeline')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Workstation) {
    return (
      <WorkstationSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.workstationId}`,
          };
        }}
        additionalOptions={[getWildCardOption('workstation')]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.Webhook) {
    return (
      <WebhookSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(value) => {
          return {
            label: value.name,
            value: `${valuePrefix}:${value.webhookConfigurationId}`,
          };
        }}
        additionalOptions={[getWildCardOption('webhook')]}
        required
      />
    );
  }

  return null;
};
